import {
    AiprizeBusinessScope,
    AiprizeBusinessScopeSchema,
    AiprizeRegistrationRecord,
    AiprizeRegistrationRecordSchema,
    AiprizeRelatedCompany,
    AiprizeRelatedCompanySchema,
    AiprizeRelatedPerson,
    AiprizeRelatedPersonSchema,
} from '@40seas/entities/aiprize';
import { EnumSchema, Executor, FromSchema } from '@40seas/entities/common';
import { BaseEntity, WithBaseEntitySchema } from '@40seas/models';
import { z } from 'zod';
import {
    VerifiedBusinessDetails,
    VerifiedBusinessDetailsSchema,
} from '../types/business-verification.ts/credit-review.business-verification-verified-business-details';
import { CreditSafeReportShareCapital, CreditSafeReportShareCapitalSchema } from '@40seas/entities/credit-safe';

export enum CreditReviewInternationalScore {
    A = 'A',
    B = 'B',
    C = 'C',
    D = 'D',
    E = 'E',
    NotRated = 'Not Rated',
    BankruptcyProceedingsFiled = 'Bankruptcy proceedings filed',
}

export const CreditReviewInternationalScoreOrder: Record<CreditReviewInternationalScore, number> = {
    [CreditReviewInternationalScore.A]: 5,
    [CreditReviewInternationalScore.B]: 4,
    [CreditReviewInternationalScore.C]: 3,
    [CreditReviewInternationalScore.D]: 2,
    [CreditReviewInternationalScore.E]: 1,
    [CreditReviewInternationalScore.NotRated]: 0,
    [CreditReviewInternationalScore.BankruptcyProceedingsFiled]: -1,
};

export const CreditScoreSchema = z
    .object({
        value: z.number(),
    })
    .strict();

export const InternationalScoreSchema = z
    .object({
        value: EnumSchema(CreditReviewInternationalScore),
    })
    .strict();

export const RecommendedCreditLimitSchema = z
    .object({
        amount: z.number(),
        currency: z.string(),
    })
    .strict();

export class CreditScore extends FromSchema(CreditScoreSchema) {
    value: number;
}

export class InternationalScore extends FromSchema(InternationalScoreSchema) {
    value: CreditReviewInternationalScore;
}

export class RecommendedCreditLimit extends FromSchema(RecommendedCreditLimitSchema) {
    amount: number;
    currency: string;
}

export enum CreditReviewNotesStatus {
    Pending = 'pending',
    Done = 'done',
}

export const CreditReviewNotesSchema = WithBaseEntitySchema(
    z.object({
        status: EnumSchema(CreditReviewNotesStatus),
        buyer: z.string(),
        summary: z.string().optional(),
        verifiedBusinessDetails: VerifiedBusinessDetailsSchema.optional(),
        relatedPersons: z.array(AiprizeRelatedPersonSchema).optional(),
        relatedCompanies: z.array(AiprizeRelatedCompanySchema).optional(),
        businessScope: AiprizeBusinessScopeSchema.optional(),
        registrationRecords: z.array(AiprizeRegistrationRecordSchema).optional(),
        creditScore: CreditScoreSchema.optional(),
        internationalScore: InternationalScoreSchema.optional(),
        recommendedCreditLimit: RecommendedCreditLimitSchema.optional(),
        shareCapital: CreditSafeReportShareCapitalSchema.optional(),
    }),
).strict();

export class CreditReviewNotes extends FromSchema(CreditReviewNotesSchema) implements BaseEntity {
    id: string;
    status: CreditReviewNotesStatus;
    buyer: string;
    summary?: string;
    verifiedBusinessDetails?: VerifiedBusinessDetails;
    relatedPersons?: AiprizeRelatedPerson[];
    relatedCompanies?: AiprizeRelatedCompany[];
    businessScope?: AiprizeBusinessScope;
    registrationRecords?: AiprizeRegistrationRecord[];
    creditScore?: CreditScore;
    internationalScore?: InternationalScore;
    recommendedCreditLimit?: RecommendedCreditLimit;
    shareCapital?: CreditSafeReportShareCapital;

    createdAt: Date;
    updatedAt: Date;
    tenantId: string;
    seller: string;
    createdBy: Executor;
    metadata?: { [key: string]: string };
    timeline?: { [status: string]: { updatedAt: Date } };
}
